import type { LayoutConfig } from '../../engine/plant/types';

/** P&ID for the CSTR unit. Feed left, reactor centre, separation right, utilities below. */
export const layout: LayoutConfig = {
  views: [
    {
      id: 'overview',
      name: 'Unit overview',
      viewBox: [0, 0, 1400, 860],
      nodes: [
        { equipment: 'SRC-A', x: 60, y: 180, label: 'Feed A' },
        { equipment: 'SRC-B', x: 60, y: 320, label: 'Feed B' },
        { equipment: 'FV-101', x: 175, y: 180 },
        { equipment: 'FV-102', x: 175, y: 320 },
        { equipment: 'HD-101', x: 265, y: 250 },
        { equipment: 'FV-103', x: 360, y: 130 },
        { equipment: 'HX-103', x: 360, y: 250 },
        { equipment: 'SRC-Q', x: 700, y: 130, label: 'Quench' },
        { equipment: 'FV-901', x: 700, y: 225 },
        { equipment: 'R-201', x: 560, y: 380 },
        { equipment: 'FV-203', x: 700, y: 400 },
        { equipment: 'V-301', x: 880, y: 380 },
        { equipment: 'FV-301', x: 880, y: 240 },
        { equipment: 'FV-302', x: 1010, y: 430 },
        { equipment: 'TK-401', x: 1130, y: 470 },
        { equipment: 'SNK-PROD', x: 1290, y: 470, label: 'To export' },
        { equipment: 'SRC-CW', x: 300, y: 730, label: 'CW supply' },
        { equipment: 'CWP-201', x: 400, y: 730 },
        { equipment: 'FV-202', x: 500, y: 730 },
        { equipment: 'SNK-CW', x: 700, y: 730, label: 'CW return' },
      ],
      pipes: [
        { id: 'p-a', points: [86, 180, 157, 180], flowTag: 'S-101.Q', label: 'Feed A' },
        { id: 'p-b', points: [86, 320, 157, 320], flowTag: 'S-102.Q', label: 'Feed B' },
        { id: 'p-a2', points: [193, 180, 258, 180, 258, 250], flowTag: 'S-101.Q' },
        { id: 'p-b2', points: [193, 320, 258, 320, 258, 250], flowTag: 'S-102.Q' },
        { id: 'p-hd', points: [272, 250, 322, 250], flowTag: 'S-101.Q' },
        { id: 'p-oil', points: [360, 154, 360, 230], flowTag: 'HX-103.UTIL', utility: true, label: 'Hot oil' },
        { id: 'p-feed', points: [398, 250, 468, 250, 468, 360, 528, 360], flowTag: 'S-101.Q' },
        { id: 'p-quench', points: [700, 150, 700, 201], flowTag: 'CALC-901.OUT', utility: true },
        { id: 'p-quench2', points: [700, 249, 700, 300, 596, 300, 596, 346], flowTag: 'CALC-901.OUT', utility: true, label: 'Quench' },
        { id: 'p-out', points: [592, 400, 682, 400], flowTag: 'S-203.Q' },
        { id: 'p-sep', points: [718, 400, 834, 400], flowTag: 'S-203.Q' },
        { id: 'p-vent', points: [880, 358, 880, 264], flowTag: 'V-301.VAP', utility: true },
        { id: 'p-vent2', points: [880, 216, 880, 170], flowTag: 'V-301.VAP', utility: true, label: 'To flare' },
        { id: 'p-liq', points: [926, 412, 992, 412, 992, 430], flowTag: 'S-302.Q' },
        { id: 'p-prod', points: [1028, 430, 1090, 430, 1090, 470, 1098, 470], flowTag: 'S-302.Q', label: 'Product' },
        { id: 'p-exp', points: [1162, 470, 1264, 470], flowTag: 'S-302.Q' },
        { id: 'p-cw1', points: [326, 730, 383, 730], flowTag: 'S-CW.Q', label: 'Cooling water' },
        { id: 'p-cw2', points: [417, 730, 482, 730], flowTag: 'S-CW.Q' },
        { id: 'p-cw3', points: [518, 730, 545, 730, 545, 416], flowTag: 'S-CW.Q' },
        { id: 'p-cw4', points: [575, 416, 620, 416, 620, 730, 674, 730], flowTag: 'S-CW.Q' },
      ],
      bubbles: [
        { ref: 'FIC-101', x: 175, y: 112, leaderTo: [175, 156], shared: true },
        { ref: 'FT-101', x: 118, y: 112, leaderTo: [160, 168] },
        { ref: 'FFIC-102', x: 175, y: 392, leaderTo: [175, 344], shared: true },
        { ref: 'FT-102', x: 118, y: 392, leaderTo: [160, 332] },
        { ref: 'TIC-103', x: 428, y: 130, leaderTo: [382, 200], shared: true },
        { ref: 'TT-103', x: 428, y: 190, leaderTo: [396, 232] },
        { ref: 'TIC-201', x: 452, y: 268, leaderTo: [524, 340], shared: true },
        { ref: 'TT-201', x: 508, y: 268, leaderTo: [548, 340] },
        { ref: 'AT-201', x: 620, y: 268, leaderTo: [580, 340] },
        { ref: 'LT-203', x: 664, y: 300, leaderTo: [592, 356] },
        { ref: 'LIC-203', x: 720, y: 336, leaderTo: [704, 378], shared: true },
        { ref: 'FIC-202', x: 466, y: 636, leaderTo: [500, 706], shared: true },
        { ref: 'FT-202', x: 534, y: 636, leaderTo: [545, 700] },
        { ref: 'TT-202', x: 620, y: 636, leaderTo: [620, 700] },
        { ref: 'TT-204', x: 300, y: 656, leaderTo: [300, 708] },
        { ref: 'PIC-301', x: 806, y: 240, leaderTo: [858, 244], shared: true },
        { ref: 'PT-301', x: 946, y: 300, leaderTo: [900, 360] },
        { ref: 'LT-302', x: 880, y: 470, leaderTo: [880, 404] },
        { ref: 'LIC-302', x: 1010, y: 336, leaderTo: [1010, 408], shared: true },
        { ref: 'TT-301', x: 812, y: 470, leaderTo: [846, 408] },
        { ref: 'FT-302', x: 1096, y: 380, leaderTo: [1080, 424] },
        { ref: 'AT-401', x: 1130, y: 590, leaderTo: [1130, 512] },
        { ref: 'ZT-901', x: 762, y: 225, leaderTo: [718, 225] },
      ],
      labels: [
        { x: 200, y: 60, text: 'FEED AND PREHEAT', size: 13, anchor: 'middle', muted: true },
        { x: 580, y: 500, text: 'EXOTHERMIC REACTOR', size: 13, anchor: 'middle', muted: true },
        { x: 940, y: 130, text: 'SEPARATION', size: 13, anchor: 'middle', muted: true },
        { x: 500, y: 800, text: 'JACKET COOLING WATER', size: 13, anchor: 'middle', muted: true },
      ],
      values: [
        { tag: 'R-201.CONV', x: 60, y: 560, label: 'Conversion', units: '%', decimals: 2, anchor: 'start' },
        { tag: 'AT-201.PV', x: 60, y: 582, label: 'Unreacted A', units: 'kmol/m³', decimals: 4, anchor: 'start' },
        { tag: 'R-201.QRXN', x: 60, y: 604, label: 'Reaction heat', units: 'kW', decimals: 1, anchor: 'start' },
        { tag: 'R-201.QJKT', x: 60, y: 626, label: 'Jacket duty', units: 'kW', decimals: 1, anchor: 'start' },
        { tag: 'R-201.UA', x: 60, y: 648, label: 'Jacket UA', units: 'W/K', decimals: 0, anchor: 'start' },
        { tag: 'TT-202.PV', x: 900, y: 640, label: 'Jacket outlet', units: 'K', decimals: 2, anchor: 'start' },
        { tag: 'TT-204.PV', x: 900, y: 662, label: 'CW supply', units: 'K', decimals: 2, anchor: 'start' },
        { tag: 'FT-202.PV', x: 900, y: 684, label: 'CW flow', units: 'm³/h', decimals: 2, anchor: 'start' },
        { tag: 'AT-401.PV', x: 900, y: 706, label: 'Product B', units: 'kmol/m³', decimals: 3, anchor: 'start' },
      ],
    },
  ],
};
